/**
 * shared/war_sky.ts — CLIENT MIRROR of server/src/lib/war_sky.ts (the server copy is authoritative; keep them
 * byte-identical below the headers). Space campaign slice L (2026-10-01): the war sky summary — one cycle's fleet war
 * as the ground sees it, ≤ 1 KiB, ≤ 98 silhouettes. Pure.
 */

/** The rollout flag: false stops the summary writer (the ground's sky then reads the clock alone). */
export const WAR_SKY_FLAG = 'war_sky_summary_v1';
/** How many cycles of summaries the table keeps (two days of hourly wars — the newest is all the sky reads). */
export const WAR_SKY_KEEP_CYCLES = 48;
/** The report's budget (§10.4): at most 98 silhouettes — 10 capitals + 3 frigates + 6 corvettes + 30 gunships a side. */
export const WAR_SKY_MAX_SHIPS = 98;
/** And at most 1 KiB of summary a campaign. */
export const WAR_SKY_MAX_BYTES = 1024;

export type WarSkyState = 'engaged' | 'retreat' | 'resolved' | 'ceasefire';

/** What the writer reads (plain numbers — the caller converts the SDK's bigints and Timestamps). */
export interface WarSkyInput {
  cyc: number;
  /** THIS cycle's campaign row, or null (a ceasefire hour, or the sweep has not opened it yet). */
  campaign: {
    campaignId: number; fortKey: string; targetSystem: string; state: string;
    openedAtS: number; endsAtS: number; winner: string; retreatSide: number; rounds: number;
  } | null;
  /** The two sides' rows (capitals and the gunship POOL — `capsAlive` is the roster-derived count). */
  sides: ReadonlyArray<{ side: number; capsCommitted: number; capsAlive: number; skirmAlive: number }>;
  /** The escort roster's rows. */
  escorts: ReadonlyArray<{ side: number; cls: string; alive: boolean }>;
  /** A standing ceasefire covers this cycle (THE TREATY LAW). */
  ceasefire: boolean;
  /** The physical fort when no row names it (`fortOf(cyc).key`). */
  fortKey: string;
  /** Slice D pinned this war: its named lives are judged by the server. */
  validated: boolean;
}

/** One cycle's sky summary — the `campaign_sky` row's payload. */
export interface WarSky {
  cyc: number;
  campaignId: number;
  state: WarSkyState;
  fort: string;
  front: string;
  openedAtS: number;
  endsAtS: number;
  caps: [number, number];
  capsCommitted: [number, number];
  frigates: [number, number];
  corvettes: [number, number];
  gunships: [number, number];
  retreatSide: number;
  winner: string;
  validated: boolean;
  rounds: number;
}

const u8 = (v: unknown): number => {
  const n = typeof v === 'number' && Number.isFinite(v) ? Math.floor(v) : 0;
  return Math.max(0, Math.min(255, n));
};

/** Build the summary (pure). Null when nothing is happening this cycle (no row and no ceasefire — the clock decides). */
export function buildWarSky(i: WarSkyInput): WarSky | null {
  const c = i.campaign;
  if (!c && !i.ceasefire) return null;
  const caps: [number, number] = [0, 0], committed: [number, number] = [0, 0], gun: [number, number] = [0, 0];
  const fri: [number, number] = [0, 0], cor: [number, number] = [0, 0];
  if (c) {
    for (const s of i.sides) {
      if (s.side !== 0 && s.side !== 1) continue;
      caps[s.side] = u8(s.capsAlive); committed[s.side] = u8(s.capsCommitted); gun[s.side] = u8(s.skirmAlive);
    }
    for (const e of i.escorts) {
      if (!e.alive || (e.side !== 0 && e.side !== 1)) continue;
      if (e.cls === 'frigate') fri[e.side] = u8(fri[e.side] + 1);
      else if (e.cls === 'corvette') cor[e.side] = u8(cor[e.side] + 1);
    }
  }
  const st = c ? c.state : 'ceasefire';
  const state: WarSkyState = st === 'retreat' || st === 'resolved' || st === 'ceasefire' ? st : 'engaged';
  return {
    cyc: Math.max(0, Math.floor(i.cyc)),
    campaignId: c ? Math.max(0, Math.floor(c.campaignId)) : 0,
    state,
    fort: (c ? c.fortKey : i.fortKey).slice(0, 24),
    front: c ? c.targetSystem.slice(0, 24) : '',
    openedAtS: c ? Math.floor(c.openedAtS) : 0,
    endsAtS: c ? Math.floor(c.endsAtS) : 0,
    caps, capsCommitted: committed, frigates: fri, corvettes: cor, gunships: gun,
    retreatSide: c && (c.retreatSide === 0 || c.retreatSide === 1) ? c.retreatSide : -1,
    winner: c ? c.winner.slice(0, 16) : '',
    validated: !!c && i.validated,
    rounds: c ? Math.max(0, Math.min(65535, Math.floor(c.rounds))) : 0,
  };
}

/** The change signature: the writer rewrites a row only when this moves (≤ one write per change, never per tick). */
export function warSkySignature(s: WarSky): string {
  return [s.campaignId, s.state, s.fort, s.front, s.openedAtS, s.endsAtS, s.caps.join(','), s.capsCommitted.join(','),
    s.frigates.join(','), s.corvettes.join(','), s.gunships.join(','), s.retreatSide, s.winner, s.validated ? 1 : 0, s.rounds].join('|');
}

/** The summary's size on the wire as JSON (the ≤ 1 KiB budget is judged on this). */
export function warSkyBytes(s: WarSky): number {
  return JSON.stringify(s).length;
}

/** How many ships of each class the sky draws, within the 98 budget (gunships trimmed first, then corvettes). */
export function silhouetteBudget(s: Pick<WarSky, 'caps' | 'frigates' | 'corvettes' | 'gunships'>): { caps: [number, number]; frigates: [number, number]; corvettes: [number, number]; gunships: [number, number]; total: number } {
  const caps: [number, number] = [Math.min(10, s.caps[0]), Math.min(10, s.caps[1])];
  const fri: [number, number] = [Math.min(3, s.frigates[0]), Math.min(3, s.frigates[1])];
  const cor: [number, number] = [Math.min(6, s.corvettes[0]), Math.min(6, s.corvettes[1])];
  const gun: [number, number] = [Math.min(30, s.gunships[0]), Math.min(30, s.gunships[1])];
  let total = caps[0] + caps[1] + fri[0] + fri[1] + cor[0] + cor[1] + gun[0] + gun[1];
  for (const k of [gun, cor] as Array<[number, number]>) {
    while (total > WAR_SKY_MAX_SHIPS && (k[0] > 0 || k[1] > 0)) {
      const sIdx = k[0] >= k[1] ? 0 : 1;
      k[sIdx]--; total--;
    }
  }
  return { caps, frigates: fri, corvettes: cor, gunships: gun, total };
}
